import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface SettingsSlideModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle?: React.ReactNode;
    icon?: React.ReactNode;
    children: React.ReactNode;
    width?: string; // Custom width (default: 672px)
}

export default function SettingsSlideModal({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    children,
    width = '672px'
}: SettingsSlideModalProps) {
    const [shouldRender, setShouldRender] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        if (isOpen && !shouldRender) {
            setShouldRender(true);
            // Delay the animation start
            setTimeout(() => {
                setIsAnimating(true);
            }, 50);
        } else if (!isOpen && shouldRender) {
            setIsAnimating(false);
            // Wait for animation to finish before unmounting
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!shouldRender) return null;

    return (
        <div className="fixed inset-0 z-[9999]">
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-black/70 transition-opacity duration-300 cursor-pointer ${
                    isAnimating ? 'opacity-100' : 'opacity-0'
                }`}
            />

            {/* Sidebar - positioned at extreme right */}
            <div className={`fixed top-0 right-0 h-full bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 ease-out ${
                isAnimating ? 'translate-x-0' : 'translate-x-full'
            }`} style={{ width }}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                            {icon && (
                                <div className="text-blue-600 dark:text-blue-400">
                                    {icon}
                                </div>
                            )}
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {title}
                                </h2>
                                {subtitle && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                        {subtitle}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            aria-label="Close modal"
                        >
                            <X className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}